import { useEffect, useState } from 'react';

/**
 * Serializes announcements into a single sr-only `aria-live` region so
 * multiple independent sources — e.g. several Maeve chat surfaces mounted
 * at once on `ManagerCaseDetail`, only one of which is the active tab —
 * can share one region without one silently overwriting another. A plain
 * `useState` + direct `setState(text)` from each source would let two
 * same-tick calls collapse into a single React commit, dropping whichever
 * announcement lost the race with no screen-reader output at all.
 *
 * `announce` queues the message (a functional `setState` update, so
 * concurrent same-tick calls append rather than clobber each other). A
 * short delay between dequeues, rather than announcing on the very next
 * tick, guarantees each item lands as its own distinct DOM mutation —
 * back-to-back same-tick `aria-live` mutations can otherwise coalesce
 * into a single spoken update in some screen readers.
 */
export function useLiveAnnouncer(): { message: string; announce: (text: string) => void } {
  const [queue, setQueue] = useState<string[]>([]);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (queue.length === 0) return;
    // Clear first so back-to-back identical text still re-triggers the
    // live region — an unchanged `aria-live` value doesn't re-announce.
    setMessage('');
    const timer = setTimeout(() => {
      const [next, ...rest] = queue;
      setMessage(next ?? '');
      setQueue(rest);
    }, 50);
    return () => clearTimeout(timer);
  }, [queue]);

  function announce(text: string) {
    setQueue((q) => [...q, text]);
  }

  return { message, announce };
}
